"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, MapPin, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function BrandLogo({
  variant = "dark",
  iconOnly = false,
  className = ""
}: {
  variant?: "light" | "dark";
  iconOnly?: boolean;
  className?: string;
}) {
  const dark = variant === "dark";
  return <span className={"inline-flex items-center gap-3 " + className}>
      <svg width="46" height="46" viewBox="0 0 64 64" aria-hidden="true" className="shrink-0">
        <defs>
          <linearGradient id="ftrTop" x1="10" y1="14" x2="54" y2="40" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#2A4A7C" />
            <stop offset="1" stopColor="#17304F" />
          </linearGradient>
          <linearGradient id="ftrLeft" x1="10" y1="26" x2="32" y2="58" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#10203A" />
            <stop offset="1" stopColor="#070F1D" />
          </linearGradient>
          <linearGradient id="ftrRight" x1="54" y1="26" x2="32" y2="58" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#1B3558" />
            <stop offset="1" stopColor="#0C1B30" />
          </linearGradient>
          <linearGradient id="ftrDrop" x1="24" y1="4" x2="42" y2="30" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#FFD37A" />
            <stop offset="0.5" stopColor="#F6B63C" />
            <stop offset="1" stopColor="#D8860C" />
          </linearGradient>
          <radialGradient id="ftrShine" cx="0" cy="0" r="1" gradientTransform="translate(27 14) rotate(58) scale(9 6)" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="32" cy="59" rx="19" ry="3.4" fill="#000000" opacity="0.18" />
        <path d="M32 20 54 32.5 32 45 10 32.5Z" fill={"url(#ftrTop)"} />
        <path d="M10 32.5 32 45v12.5L10 45Z" fill={"url(#ftrLeft)"} />
        <path d="M54 32.5 32 45v12.5L54 45Z" fill={"url(#ftrRight)"} />
        <path d="M32 20 54 32.5 32 45 10 32.5Z" fill="none" stroke="#3C6197" strokeOpacity="0.55" strokeWidth="0.9" />
        <path d="M32 45v12.5" stroke="#000000" strokeOpacity="0.35" strokeWidth="0.9" />
        <path d="M22 32.5 32 38.2 42 32.5" fill="none" stroke="#F6B63C" strokeOpacity="0.9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M32 3c0 0 11 12.4 11 19a11 11 0 0 1-22 0C21 15.4 32 3 32 3Z" fill={"url(#ftrDrop)"} />
        <path d="M32 3c0 0 11 12.4 11 19a11 11 0 0 1-22 0C21 15.4 32 3 32 3Z" fill="none" stroke="#B8700A" strokeOpacity="0.45" strokeWidth="0.8" />
        <path d="M32 3c0 0 11 12.4 11 19a11 11 0 0 1-22 0C21 15.4 32 3 32 3Z" fill={"url(#ftrShine)"} />
        <path d="M27.5 24.5a4.5 4.5 0 0 0 4.5 4.5" fill="none" stroke="#ffffff" strokeOpacity="0.75" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      {!iconOnly && <span className="flex flex-col leading-none">
          <span className={"text-[19px] font-bold tracking-tight " + (dark ? "text-white" : "text-slate-900")}>Jonesboro</span>
          <span className={"mt-1 text-[9.5px] font-semibold uppercase tracking-[0.17em] " + (dark ? "text-amber-400" : "text-amber-600")}>Emergency Plumber</span>
        </span>}
    </span>;
}
function FooterMenuRecursiveList({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={`flex flex-col gap-2.5 ${depth > 0 ? "pl-3 border-l border-border mt-1" : ""}`}>
      {items.map((item, index) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      return <li key={item.id || `${item.label}-${index}`} data-index={index} className="text-sm">
            <Link to={item.href || "#"} className="text-muted-foreground hover:text-foreground font-medium transition-colors inline-flex items-center gap-1.5 group">
              <span className="h-1.5 w-1.5 rounded-full bg-accent opacity-0 group-hover:opacity-100 transition-opacity" />
              <span>{item.label}</span>
            </Link>
            {hasChildren && item.children && <FooterMenuRecursiveList items={item.children} depth={depth + 1} />}
          </li>;
    })}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2.5">
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="flex flex-col gap-2.5">
        <li data-index={0}>
          <Link to="/" className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors">
            Home
          </Link>
        </li>
        <li data-index={1}>
          <Link to="/services/" className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors">
            Plumbing Services
          </Link>
        </li>
        <li data-index={2}>
          <Link to="/problems/" className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors">
            Plumbing Problems
          </Link>
        </li>
        <li data-index={3}>
          <Link to="/contact/" className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors">
            Contact Us
          </Link>
        </li>
      </ul>;
  }
  return <FooterMenuRecursiveList items={menuItems} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border pt-16 pb-12 overflow-hidden">
      <div className="max-w-[1240px] mx-auto px-5 md:px-10">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-12">
          {/* Brand and Emergency Dispatch Column */}
          <motion.div variants={itemVariants} className="lg:col-span-4 flex flex-col items-start gap-5">
            <Link to="/" className="inline-block transition-opacity hover:opacity-90">
              <BrandLogo className="" />
            </Link>

            <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
              Rapid-response emergency plumbing in Jonesboro, GA. Dedicated local master plumbers solving burst pipes, severe drain clogs, and water heater failures 24/7.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto pt-1">
              <Button asChild className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 shadow-sm rounded-full h-11 px-6 transition-[background-color,box-shadow]">
                <a href="tel:4708479076" className="flex items-center gap-2">
                  <Phone className="size-4 shrink-0 text-accent-foreground" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </motion.div>

          {/* Quick Navigation Links */}
          <motion.div variants={itemVariants} className="lg:col-span-3 flex flex-col gap-4">
            <h3 className="font-default text-base font-bold text-foreground tracking-tight flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              Quick Navigation
            </h3>
            <MenuProvider menu_id="17" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </motion.div>

          {/* Service Area & Operational Coverage */}
          <motion.div variants={itemVariants} className="lg:col-span-3 flex flex-col gap-4">
            <h3 className="font-default text-base font-bold text-foreground tracking-tight flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-accent" />
              Service Area & Hours
            </h3>

            <div className="flex flex-col gap-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2.5">
                <MapPin className="size-4 shrink-0 text-primary mt-0.5" />
                <span>
                  <strong className="text-foreground font-semibold">Primary Zone:</strong> Jonesboro, GA & Clayton County surrounding areas.
                </span>
              </div>

              <div className="flex items-start gap-2.5">
                <Clock className="size-4 shrink-0 text-primary mt-0.5" />
                <span>
                  <strong className="text-foreground font-semibold">Availability:</strong> 24/7 Emergency Dispatch, including weekends & holidays.
                </span>
              </div>

              <div className="flex items-start gap-2.5">
                <ShieldCheck className="size-4 shrink-0 text-primary mt-0.5" />
                <span>Licensed, insured, and verified upfront pricing with zero hidden charges.</span>
              </div>
            </div>
          </motion.div>

          {/* Urgent Assistance Triage Box */}
          <motion.div variants={itemVariants} className="lg:col-span-2 flex flex-col gap-4">
            <div className="rounded-xl border border-border bg-card p-4.5 flex flex-col gap-3 shadow-xs">
              <span className="text-xs font-bold uppercase tracking-wider text-accent-foreground bg-accent/20 px-2.5 py-1 rounded-full w-fit">
                Immediate Help
              </span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Facing an active leak or overflowing drain right now? Direct phone calls get priority truck dispatch.
              </p>
              <Button asChild variant="outline" size="sm" className="w-full rounded-full border-border hover:border-primary hover:text-primary justify-between text-xs font-semibold">
                <Link to="/contact/">
                  <span>Service Request</span>
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>

        <Separator className="my-8 bg-border" />

        {/* Bottom Bar: Copyright and Legal */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>
            {"© "}{new Date().getFullYear()}{" Jonesboro Emergency Plumber. All rights reserved."}
          </p>

          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5">
              <span className="size-2 rounded-full bg-accent animate-pulse" />
              Direct Phone Support Only
            </span>
          </div>
        </div>
      </div>
    </section>;
}